import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  CreditCard, ExternalLink, CheckCircle, XCircle,
  Loader2, Building2, Plus, Pencil, Trash2,
  Clock, FileText, Copy
} from 'lucide-react';

const STATI = {
  in_attesa: { label: 'In Attesa', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  approvata: { label: 'Approvata', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  rifiutata: { label: 'Rifiutata', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

const FinanziaPolizza = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [compassOpen, setCompassOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPratica, setSelectedPratica] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [pratiche, setPratiche] = useState([]);
  const [stripeConfig, setStripeConfig] = useState({});
  const [compassForm, setCompassForm] = useState({ cliente_nome: '', descrizione: '', importo: '', note: '' });
  const [stripeForm, setStripeForm] = useState({ cliente_nome: '', descrizione: '', importo: '', note: '' });
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [filterTipo, setFilterTipo] = useState('');

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') { setPaymentResult({ status: 'paid' }); fetchPratiche(); setSearchParams({}); }
    else if (status === 'cancelled') { setPaymentResult({ status: 'cancelled' }); setSearchParams({}); }
    fetchPratiche();
    fetchStripeConfig();
  }, []);

  const fetchStripeConfig = async () => {
    try { const res = await api.get('/payments/config'); setStripeConfig(res.data || {}); } catch {}
  };

  const fetchPratiche = async () => {
    try {
      const params = filterTipo ? `?tipo=${filterTipo}` : '';
      const res = await api.get(`/payments/pratiche${params}`);
      setPratiche(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchPratiche(); }, [filterTipo]);

  const handleCompassCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments/pratiche', { tipo: 'compass', cliente_nome: compassForm.cliente_nome, descrizione: compassForm.descrizione, importo: parseFloat(compassForm.importo) || 0, note: compassForm.note });
      toast.success('Pratica Compass creata');
      setCompassOpen(false);
      setCompassForm({ cliente_nome: '', descrizione: '', importo: '', note: '' });
      fetchPratiche();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleStripeCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments/pratiche', { tipo: 'stripe', cliente_nome: stripeForm.cliente_nome, descrizione: stripeForm.descrizione, importo: parseFloat(stripeForm.importo) || 0, note: stripeForm.note });
      toast.success('Pratica Stripe registrata! Ora puoi procedere al pagamento.');
      setStripeDialogOpen(false);
      setStripeForm({ cliente_nome: '', descrizione: '', importo: '', note: '' });
      fetchPratiche();
      // Redirect to payment link
      if (stripeConfig.payment_link) {
        window.open(stripeConfig.payment_link, '_blank');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleUpdatePratica = async () => {
    if (!selectedPratica) return;
    try {
      await api.put(`/payments/pratiche/${selectedPratica.id}`, editForm);
      toast.success('Pratica aggiornata');
      setDetailOpen(false);
      fetchPratiche();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeletePratica = async (id) => {
    if (!window.confirm('Eliminare questa pratica?')) return;
    try { await api.delete(`/payments/pratiche/${id}`); toast.success('Eliminata'); setDetailOpen(false); fetchPratiche(); } catch { toast.error('Errore'); }
  };

  const openDetail = (p) => {
    setSelectedPratica(p);
    setEditForm({ stato: p.stato, note: p.note || '', descrizione: p.descrizione || '', importo: p.importo || 0 });
    setDetailOpen(true);
  };

  const fmt = (v) => v ? `${Number(v).toFixed(2)} €` : '-';

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="finanzia-polizza-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Finanzia Polizza</h1>
        <p className="text-slate-500 mt-1">Pagamento online, finanziamento e tracciamento pratiche</p>
      </div>

      {paymentResult && (
        <div className={`mb-5 p-4 rounded-xl border flex items-center gap-3 ${paymentResult.status === 'paid' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="payment-result">
          {paymentResult.status === 'paid' ? <CheckCircle className="text-emerald-600" size={24} /> : <XCircle className="text-red-500" size={24} />}
          <p className="font-semibold text-sm">{paymentResult.status === 'paid' ? 'Pagamento completato!' : 'Pagamento annullato'}</p>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setPaymentResult(null)}>Chiudi</Button>
        </div>
      )}

      {/* Two cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        {/* Stripe */}
        <div className="rounded-2xl border bg-white">
          <div className="h-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-2xl" />
          <div className="p-3 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard size={28} className="text-indigo-600" />
              <div>
                <h3 className="font-semibold text-slate-900">Pagamento Stripe</h3>
                <p className="text-xs text-slate-500">Paga online o invia link al cliente</p>
              </div>
            </div>
            {stripeConfig.payment_link && (
              <div className="flex gap-2">
                <Dialog open={stripeDialogOpen} onOpenChange={setStripeDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="payment-link-btn">
                      <CreditCard size={16} className="mr-2" /> Nuovo Pagamento
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Nuovo Pagamento Stripe</DialogTitle></DialogHeader>
                    <form onSubmit={handleStripeCreate} className="space-y-4 pt-2">
                      <div><Label>Nome Cliente *</Label><Input value={stripeForm.cliente_nome} onChange={e => setStripeForm(p => ({...p, cliente_nome: e.target.value}))} required placeholder="Mario Rossi" data-testid="stripe-client-input" /></div>
                      <div><Label>Descrizione</Label><Input value={stripeForm.descrizione} onChange={e => setStripeForm(p => ({...p, descrizione: e.target.value}))} placeholder="Pagamento polizza auto" /></div>
                      <div><Label>Importo (€)</Label><Input type="number" step="0.01" value={stripeForm.importo} onChange={e => setStripeForm(p => ({...p, importo: e.target.value}))} placeholder="500.00" data-testid="stripe-amount-input" /></div>
                      <div><Label>Note</Label><Textarea value={stripeForm.note} onChange={e => setStripeForm(p => ({...p, note: e.target.value}))} placeholder="Note..." /></div>
                      <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="stripe-submit"><CreditCard size={16} className="mr-2" /> Registra e Paga</Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" title="Copia link per il cliente" onClick={() => {
                  navigator.clipboard.writeText(stripeConfig.payment_link);
                  toast.success('Link copiato negli appunti!');
                }} data-testid="copy-link-btn">
                  <Copy size={16} />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Compass */}
        <div className="rounded-2xl border bg-white">
          <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-2xl" />
          <div className="p-3 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <Building2 size={28} className="text-emerald-600" />
              <div>
                <h3 className="font-semibold text-slate-900">Finanziamento Compass</h3>
                <p className="text-xs text-slate-500">Crea pratica e traccia approvazione</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Dialog open={compassOpen} onOpenChange={setCompassOpen}>
                <DialogTrigger asChild>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="open-compass-btn">
                    <Plus size={16} className="mr-2" /> Nuova Pratica
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Nuova Pratica Compass</DialogTitle></DialogHeader>
                  <form onSubmit={handleCompassCreate} className="space-y-4 pt-2">
                    <div><Label>Nome Cliente *</Label><Input value={compassForm.cliente_nome} onChange={e => setCompassForm(p => ({...p, cliente_nome: e.target.value}))} required placeholder="Mario Rossi" data-testid="compass-client-input" /></div>
                    <div><Label>Descrizione</Label><Input value={compassForm.descrizione} onChange={e => setCompassForm(p => ({...p, descrizione: e.target.value}))} placeholder="Finanziamento polizza auto" /></div>
                    <div><Label>Importo (€)</Label><Input type="number" step="0.01" value={compassForm.importo} onChange={e => setCompassForm(p => ({...p, importo: e.target.value}))} placeholder="500.00" data-testid="compass-amount-input" /></div>
                    <div><Label>Note</Label><Textarea value={compassForm.note} onChange={e => setCompassForm(p => ({...p, note: e.target.value}))} placeholder="Note sulla pratica..." /></div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="compass-submit"><Plus size={16} className="mr-2" /> Crea Pratica</Button>
                  </form>
                </DialogContent>
              </Dialog>
              <a href="https://ari-ocp.compassonline.it/dealer-iam/app/login" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" title="Portale Compass"><ExternalLink size={16} /></Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border p-3 text-center"><p className="text-2xl font-bold text-slate-900">{pratiche.length}</p><p className="text-xs text-slate-500">Totale</p></div>
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-3 text-center"><p className="text-2xl font-bold text-amber-700">{pratiche.filter(p => p.stato === 'in_attesa').length}</p><p className="text-xs text-amber-600">In Attesa</p></div>
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3 text-center"><p className="text-2xl font-bold text-emerald-700">{pratiche.filter(p => p.stato === 'approvata').length}</p><p className="text-xs text-emerald-600">Approvate</p></div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-3 text-center"><p className="text-2xl font-bold text-red-600">{pratiche.filter(p => p.stato === 'rifiutata').length}</p><p className="text-xs text-red-500">Rifiutate</p></div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {[{ key: '', label: 'Tutte' }, { key: 'stripe', label: 'Stripe' }, { key: 'compass', label: 'Compass' }].map(f => (
            <button key={f.key} onClick={() => setFilterTipo(f.key)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filterTipo === f.key ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`} data-testid={`filter-${f.key || 'all'}`}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      {pratiche.length > 0 ? (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-50">
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Data</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Tipo</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Cliente</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Descrizione</th>
                <th className="text-right px-4 py-2.5 font-medium text-slate-600">Importo</th>
                <th className="text-center px-4 py-2.5 font-medium text-slate-600">Stato</th>
                <th className="text-right px-4 py-2.5 font-medium text-slate-600">Azioni</th>
              </tr></thead>
              <tbody>
                {pratiche.map(p => {
                  const stato = STATI[p.stato] || STATI.in_attesa;
                  const StatoIcon = stato.icon;
                  return (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(p)} data-testid={`pratica-row-${p.id}`}>
                      <td className="px-4 py-3 text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className={`text-[10px] ${p.tipo === 'stripe' ? 'border-indigo-300 text-indigo-700' : 'border-emerald-300 text-emerald-700'}`}>{p.tipo === 'stripe' ? 'Stripe' : 'Compass'}</Badge></td>
                      <td className="px-4 py-3 font-medium text-slate-800">{p.cliente_nome || '-'}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{p.descrizione || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono font-medium">{fmt(p.importo)}</td>
                      <td className="px-4 py-3 text-center"><Badge className={`${stato.color} text-[10px] gap-1`}><StatoIcon size={10} /> {stato.label}</Badge></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <button onClick={() => openDetail(p)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><Pencil size={14} /></button>
                          <button onClick={() => handleDeletePratica(p.id)} className="p-1.5 rounded hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl border"><FileText size={40} className="mx-auto mb-3 text-slate-300" /><p className="text-slate-500">Nessuna pratica trovata</p></div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Dettaglio Pratica</DialogTitle></DialogHeader>
          {selectedPratica && (
            <div className="space-y-4 pt-2">
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Tipo</span><Badge variant="outline">{selectedPratica.tipo === 'stripe' ? 'Stripe' : 'Compass'}</Badge></div>
                <div className="flex justify-between"><span className="text-slate-500">Creato da</span><span>{selectedPratica.user_nome || '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Data</span><span>{selectedPratica.created_at ? new Date(selectedPratica.created_at).toLocaleString('it-IT') : '-'}</span></div>
              </div>
              <div><Label>Stato</Label>
                <Select value={editForm.stato} onValueChange={v => setEditForm(p => ({ ...p, stato: v }))}>
                  <SelectTrigger data-testid="edit-stato-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="in_attesa">In Attesa</SelectItem><SelectItem value="approvata">Approvata</SelectItem><SelectItem value="rifiutata">Rifiutata</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Cliente</Label><div className="text-sm font-medium bg-slate-50 rounded px-3 py-2">{selectedPratica.cliente_nome}</div></div>
              <div><Label>Descrizione</Label><Input value={editForm.descrizione} onChange={e => setEditForm(p => ({ ...p, descrizione: e.target.value }))} /></div>
              <div><Label>Importo (€)</Label><Input type="number" step="0.01" value={editForm.importo} onChange={e => setEditForm(p => ({ ...p, importo: parseFloat(e.target.value) || 0 }))} /></div>
              <div><Label>Note</Label><Textarea value={editForm.note} onChange={e => setEditForm(p => ({ ...p, note: e.target.value }))} placeholder="Note..." /></div>
              <div className="flex gap-2 pt-2">
                <Button className="flex-1" onClick={handleUpdatePratica} data-testid="save-pratica-btn">Salva Modifiche</Button>
                <Button variant="outline" className="text-red-500 border-red-200" onClick={() => handleDeletePratica(selectedPratica.id)}><Trash2 size={16} /></Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanziaPolizza;
